import * as React from "react"
import * as ToastPrimitives from "@radix-ui/react-toast"
import { cva } from "class-variance-authority";
import { X } from "@phosphor-icons/react"

import { cn } from "@/lib/utils"

const ToastProvider = ToastPrimitives.Provider

const ToastViewport = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      // z-toast (500): topo da escala do DS (§5), acima do modal (400).
      "fixed top-0 z-toast flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]",
      className
    )}
    {...props} />
))
ToastViewport.displayName = ToastPrimitives.Viewport.displayName

// Painel flutuante = --radius (6px) + elevação lg, como o único aviso
// flutuante do CareOS (.aviso-versao). Entrada na curva do DS, saída na curva
// de saída (o tailwindcss-animate não herda o ease DEFAULT em animate-in/out).
const toastVariants = cva(
  "group pointer-events-auto relative flex w-full items-center justify-between space-x-2 overflow-hidden rounded-lg border p-4 pr-10 shadow-care-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=open]:ease-care data-[state=closed]:animate-out data-[state=closed]:ease-care-exit data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full",
  {
    variants: {
      variant: {
        // DS v3: sucesso = teal, erro = rust (papéis semânticos).
        default:
          "border-[color:var(--care-border-default)] bg-[var(--care-bg-surface-raised)] text-[color:var(--care-text-primary)] font-sans",
        success:
          "success group border-[color:var(--care-positive-border)] bg-[var(--care-positive-bg)] text-[color:var(--care-positive-text)] font-sans",
        destructive:
          "destructive group border-[color:var(--destructive)] bg-[var(--care-danger-bg)] text-[color:var(--care-danger-text)] font-sans",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

const Toast = React.forwardRef(({ className, variant, ...props }, ref) => {
  return (
    <ToastPrimitives.Root
      ref={ref}
      className={cn(toastVariants({ variant }), className)}
      {...props} />
  );
})
Toast.displayName = ToastPrimitives.Root.displayName

// .ui-botao do kit (fio, 32px): hover --secondary, pressionado overlay 10%,
// disabled .45 + not-allowed. O tom segue a variante do toast.
const ToastAction = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    data-variante="fio"
    data-tamanho="sm"
    className={cn(
      "ui-botao shrink-0 group-[.destructive]:border-[color:var(--destructive)] group-[.destructive]:text-[color:var(--destructive)] group-[.success]:border-[color:var(--care-positive-border)] group-[.success]:text-[color:var(--care-positive-text)]",
      className
    )}
    {...props} />
))
ToastAction.displayName = ToastPrimitives.Action.displayName

// Fechar = .ui-botao fantasma só-ícone, 32px em right-1 (ocupa 4–36px da
// borda; por isso o toast reserva pr-10 = 40px). O transition do Tailwind
// substitui o do kit, então inclui opacity junto com fundo e cor.
const ToastClose = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    aria-label="Fechar aviso"
    data-variante="fantasma"
    data-tamanho="sm"
    data-so-icone=""
    className={cn(
      "ui-botao absolute right-1 top-1 opacity-0 [@media(hover:none)]:opacity-100 transition-[opacity,background-color,color] focus-visible:opacity-100 group-hover:opacity-100 group-[.destructive]:text-[color:var(--care-danger-text)] group-[.success]:text-[color:var(--care-positive-text)]",
      className
    )}
    toast-close=""
    {...props}>
    <X size={16} weight="bold" aria-hidden="true" />
  </ToastPrimitives.Close>
))
ToastClose.displayName = ToastPrimitives.Close.displayName

const ToastTitle = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn("text-[length:var(--care-text-sm)] font-semibold", className)}
    {...props} />
))
ToastTitle.displayName = ToastPrimitives.Title.displayName

const ToastDescription = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Description ref={ref} className={cn("text-[length:var(--care-text-sm)] leading-[1.55]", className)} {...props} />
))
ToastDescription.displayName = ToastPrimitives.Description.displayName

export { ToastProvider, ToastViewport, Toast, ToastTitle, ToastDescription, ToastClose, ToastAction };
